import {
  faChevronDown,
  faCircleCheck,
  faCircleMinus
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography
} from '@mui/material';

import LegacyLimaConnectionRow from './LegacyLimaConnectionRow';
import { consentTileStyles } from './MsDefenderIntegration.styles';
import { AppType, type ConsentTileProps } from './MsDefenderIntegration.types';
import { hasConnectedDefenderIngestion } from './MsDefenderIntegration.utils';
import TenantConnectionRow from './TenantConnectionRow';

const ConsentTile = ({
  tile,
  connections,
  legacyConnections,
  allIntegrations,
  categoryStateByTenant,
  hasAdminUpdate,
  isServerStateAvailable,
  hasAnyDefenderIngestion,
  consentEligibility,
  pendingConsent,
  consentErrorTarget,
  onConsent,
  onDisconnect,
  disconnectingIntegrationId,
  disconnectErrorIntegrationId
}: ConsentTileProps) => {
  const isDefenderResponse =
    tile.appType === AppType.ENTRA_AND_DEFENDER_RESPONSE_ACTIONS;

  const isNewConsentBlocked =
    !hasAdminUpdate ||
    !isServerStateAvailable ||
    !consentEligibility.isEligible ||
    (isDefenderResponse && !hasAnyDefenderIngestion);
  const isAppConsentPending =
    pendingConsent?.appType === tile.appType &&
    pendingConsent.existingIntegrationId === undefined;
  const hasAppConsentError =
    consentErrorTarget?.appType === tile.appType &&
    consentErrorTarget.existingIntegrationId === undefined;

  return (
    <Card variant="outlined" sx={consentTileStyles.card}>
      <CardContent sx={consentTileStyles.content}>
        <Stack>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            {tile.title}
          </Typography>
          <Typography variant="subtitle2" color="textSecondary">
            {tile.description}
          </Typography>
        </Stack>

        <List dense disablePadding sx={consentTileStyles.capabilityList}>
          {tile.capabilities.map((capability) => (
            <ListItem disableGutters key={capability}>
              <ListItemIcon
                sx={{
                  color: capability.startsWith('NOT')
                    ? 'text.secondary'
                    : 'primary.main'
                }}
              >
                <FontAwesomeIcon
                  icon={
                    capability.startsWith('NOT') ? faCircleMinus : faCircleCheck
                  }
                />
              </ListItemIcon>
              <ListItemText primary={capability.replace('NOT ', '')} />
            </ListItem>
          ))}
        </List>

        {tile.requirementNote && (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ display: 'block', fontStyle: 'italic' }}
          >
            {tile.requirementNote}
          </Typography>
        )}

        {!consentEligibility.isEligible && (
          <Alert severity="info" variant="outlined">
            {consentEligibility.requirement}
          </Alert>
        )}

        {hasAppConsentError && (
          <Alert severity="error" variant="outlined">
            Unable to start Microsoft consent. Try again.
          </Alert>
        )}

        {connections.length === 0 && legacyConnections.length === 0 ? (
          <Typography variant="body2" sx={consentTileStyles.emptyConnections}>
            {isServerStateAvailable
              ? 'No tenant connections'
              : 'Tenant connection status is unavailable.'}
          </Typography>
        ) : (
          <Stack
            spacing={0}
            sx={{ borderTop: '0.0625rem solid', borderColor: 'divider' }}
          >
            {connections.map((integration) => (
              <TenantConnectionRow
                key={integration.id}
                tile={tile}
                integration={integration}
                categoryStateByTenant={categoryStateByTenant}
                hasAdminUpdate={hasAdminUpdate}
                isServerStateAvailable={isServerStateAvailable}
                hasDefenderIngestion={hasConnectedDefenderIngestion(
                  allIntegrations,
                  integration.tenant_id
                )}
                consentEligibility={consentEligibility}
                pendingConsent={pendingConsent}
                consentErrorTarget={consentErrorTarget}
                onConsent={onConsent}
                onDisconnect={onDisconnect}
                isDisconnecting={disconnectingIntegrationId === integration.id}
                hasDisconnectError={
                  disconnectErrorIntegrationId === integration.id
                }
              />
            ))}
            {legacyConnections.map((connection) => (
              <LegacyLimaConnectionRow
                key={connection.source_uid}
                connection={connection}
              />
            ))}
          </Stack>
        )}

        <Box sx={consentTileStyles.cardFooter}>
          <Accordion
            disableGutters
            elevation={0}
            sx={consentTileStyles.permissionsAccordion}
          >
            <AccordionSummary
              expandIcon={<FontAwesomeIcon icon={faChevronDown} size="sm" />}
              sx={consentTileStyles.permissionsSummary}
            >
              <Typography variant="body2" color="primary">
                View app permissions
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={consentTileStyles.permissionsDetails}>
              <Stack direction="row" sx={consentTileStyles.scopesStack}>
                {tile.requestedScopes.map((scope) => (
                  <Chip
                    key={scope}
                    label={scope}
                    size="small"
                    variant="outlined"
                    color="unknown"
                  />
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
          <Button
            variant="contained"
            size="small"
            onClick={() => onConsent({ targetTile: tile })}
            disabled={isNewConsentBlocked || isAppConsentPending}
            sx={{ flexShrink: 0 }}
          >
            {isAppConsentPending
              ? 'Opening...'
              : connections.length > 0
                ? 'Connect New Tenant'
                : 'Connect'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ConsentTile;
